import { Box, Container, Heading, Text, VStack, HStack, Icon, Flex } from "@chakra-ui/react";
import { Link } from "react-router";
import {
  HiArrowLeft,
  HiShieldCheck,
  HiLockClosed,
  HiUserGroup,
  HiDatabase,
  HiGlobe,
} from "react-icons/hi";

export default function PrivacyPolicy() {
  return (
    <Box
      //   bg="linear(to-b, gray.50, white)"
      //   minH="100vh"
      overflowY={"scroll"}
      h={"100vh"}
      w="100%"
      py={{ base: 4, md: 8 }}
      px={{ base: 4, md: 6 }}
    >
      {/* Header with Back Button */}
      <Container maxW="5xl" mb={{ base: 4, md: 6 }} px={0}>
        <Link to="/">
          <HStack
            cursor="pointer"
            color="blue.600"
            _hover={{ color: "blue.700", transform: "translateX(-4px)" }}
            transition="all 0.2s"
          >
            <Icon fontSize="xl">
              <HiArrowLeft />
            </Icon>
            <Text fontWeight="semibold">Back to Login</Text>
          </HStack>
        </Link>
      </Container>

      <Container maxW="5xl" px={0}>
        {/* Hero Section */}
        <Box
          bg="white"
          borderRadius={{ base: "xl", md: "2xl" }}
          shadow="xl"
          overflow="hidden"
          mb={{ base: 4, md: 8 }}
          borderTop="4px solid"
          borderColor="blue.500"
        >
          <Box
            bg="blue.500"
            bgGradient="linear(to-r, blue.500, purple.600)"
            p={{ base: 6, md: 8 }}
            color="white"
            textAlign="center"
          >
            <Flex justify="center" mb={4}>
              <Box bg="whiteAlpha.300" borderRadius="full" p={4} backdropFilter="blur(10px)">
                <Icon fontSize="4xl" color="white">
                  <HiShieldCheck />
                </Icon>
              </Box>
            </Flex>
            <Heading as="h1" size={{ base: "xl", md: "2xl" }} mb={2}>
              Privacy Policy
            </Heading>
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="medium" opacity={0.95}>
              SheetX - Cold Storage Management Software
            </Text>
          </Box>

          <Box p={{ base: 4, md: 8 }}>
            <HStack
              justify="center"
              gap={{ base: 4, md: 8 }}
              flexWrap="wrap"
              mb={{ base: 4, md: 6 }}
            >
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                  Version
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  1.0
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                  Effective Date
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  22/10/2025
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                  Last Reviewed
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  22/10/2025
                </Text>
              </Box>
            </HStack>

            <Box
              bg="blue.50"
              p={6}
              borderRadius={{ base: "lg", md: "xl" }}
              borderLeft="4px solid"
              borderColor="blue.500"
            >
              <Text lineHeight="tall" color="gray.700">
                Welcome to SheetX, a cloud-hosted Cold Storage Management Software. This Privacy
                Policy explains how personal, operational, and technical data is collected, used,
                stored, and protected when you use the SheetX mobile app and related web services
                (collectively referred to as the "Platform").
              </Text>
            </Box>

            <VStack align="stretch" gap={4} mt={6}>
              <Box bg="purple.50" p={5} borderRadius="lg">
                <Text lineHeight="tall" color="gray.700">
                  SheetX is developed and owned by <strong>Aura Tech Systems</strong> and is
                  published, maintained, and hosted on Google Play by{" "}
                  <strong>Krishitantra (Klonec Automation Systems Pvt. Ltd.)</strong>
                </Text>
              </Box>

              <Box bg="green.50" p={5} borderRadius="lg">
                <Text lineHeight="tall" color="gray.700">
                  By using SheetX, you agree to the terms of this Privacy Policy and consent to the
                  collection and processing of your data by both Aura Tech Systems (as the data
                  controller) and Krishitantra (Klonec Automation Systems Pvt. Ltd.) (as the data
                  processor and publisher).
                </Text>
              </Box>
            </VStack>
          </Box>
        </Box>

        {/* Main Content Sections */}
        <VStack align="stretch" gap={6}>
          {/* Section 1 */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
            borderLeft="4px solid"
            borderColor="blue.400"
          >
            <HStack mb={4}>
              <Icon fontSize="2xl" color="blue.500">
                <HiDatabase />
              </Icon>
              <Heading as="h2" size="lg" color="gray.800">
                1. Information We Collect
              </Heading>
            </HStack>

            <VStack align="stretch" gap={5}>
              <Box bg="gray.50" p={5} borderRadius="lg">
                <Heading as="h3" size="md" color="blue.600" mb={3}>
                  1.1 User Information
                </Heading>
                <VStack align="start" gap={2} pl={2}>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">Name, mobile number, and/or email address</Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">Role and designation (Owner, Admin, Staff)</Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">Organization and facility details</Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">Login credentials (securely encrypted)</Text>
                  </HStack>
                </VStack>
              </Box>

              <Box bg="gray.50" p={5} borderRadius="lg">
                <Heading as="h3" size="md" color="blue.600" mb={3}>
                  1.2 Facility & Operational Data
                </Heading>
                <VStack align="start" gap={2} pl={2}>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Cold storage facility details (ID, name, type, location, configuration)
                    </Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Crate, shelf, and chamber data including QR codes and metadata
                    </Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Customer details (name, contact, PAN/GSTIN, commodity details)
                    </Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Transaction records such as inward/outward entries, weights, and dates
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Box bg="gray.50" p={5} borderRadius="lg">
                <Heading as="h3" size="md" color="blue.600" mb={3}>
                  1.3 Device & Usage Data
                </Heading>
                <VStack align="start" gap={2} pl={2}>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Device type, operating system, app version, and crash logs
                    </Text>
                  </HStack>
                  <HStack>
                    <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                    <Text color="gray.700">
                      Usage analytics (non-personally identifiable) to improve app performance
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Box bg="gray.50" p={5} borderRadius="lg">
                <Heading as="h3" size="md" color="blue.600" mb={3}>
                  1.4 Location Data (Optional)
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  If enabled, SheetX may access approximate or precise device location to display
                  facility or customer locations on maps. Location access is optional and can be
                  disabled in device settings.
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Section 2 */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
            borderLeft="4px solid"
            borderColor="purple.400"
          >
            <HStack mb={4}>
              <Icon fontSize="2xl" color="purple.500">
                <HiUserGroup />
              </Icon>
              <Heading as="h2" size="lg" color="gray.800">
                2. How We Use Your Information
              </Heading>
            </HStack>

            <Text mb={4} color="gray.700" fontWeight="medium">
              Your information is used to:
            </Text>
            <VStack align="start" gap={2} pl={2} mb={4}>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="purple.500" />
                <Text color="gray.700">Authenticate users and manage role-based access</Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="purple.500" />
                <Text color="gray.700">
                  Operate cold storage functions including inward/outward tracking
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="purple.500" />
                <Text color="gray.700">
                  Generate reports and analytics for operational efficiency
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="purple.500" />
                <Text color="gray.700">
                  Send notifications (via FCM, SMS, or email) for system alerts or updates
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="purple.500" />
                <Text color="gray.700">
                  Improve platform performance, security, and user experience
                </Text>
              </HStack>
            </VStack>
            <Box bg="purple.50" p={5} borderRadius="lg">
              <Text lineHeight="tall" color="gray.700">
                We do not sell, rent, or trade your personal information. Analytics are anonymized,
                and users can opt out of non-essential notifications.
              </Text>
            </Box>
          </Box>

          {/* Section 3 */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
            borderLeft="4px solid"
            borderColor="green.400"
          >
            <HStack mb={4}>
              <Icon fontSize="2xl" color="green.500">
                <HiLockClosed />
              </Icon>
              <Heading as="h2" size="lg" color="gray.800">
                3. Data Controller vs. Data Processor
              </Heading>
            </HStack>

            <VStack align="stretch" gap={4}>
              <Box bg="green.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="green.700" mb={2}>
                  Data Controller
                </Text>
                <Text lineHeight="tall" color="gray.700">
                  <strong>Aura Tech Systems</strong> is the data controller, responsible for
                  determining the purposes and means of processing personal and operational data.
                </Text>
              </Box>
              <Box bg="blue.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="blue.700" mb={2}>
                  Data Processor
                </Text>
                <Text lineHeight="tall" color="gray.700">
                  <strong>Krishitantra (Klonec Automation Systems Pvt. Ltd.)</strong> is the data
                  processor, hosting, maintaining, and distributing the app on Google Play according
                  to the controller's instructions.
                </Text>
              </Box>
              <Box bg="orange.50" p={5} borderRadius="lg">
                <Text lineHeight="tall" color="gray.700" fontWeight="medium">
                  Both entities follow strict security measures to protect your data.
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Section 4 */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
            borderLeft="4px solid"
            borderColor="orange.400"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              4. Data Sharing and Disclosure
            </Heading>

            <Text mb={4} color="gray.700" fontWeight="medium">
              Data may be shared only in the following circumstances:
            </Text>
            <VStack align="stretch" gap={4}>
              <Box bg="orange.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="orange.700" mb={2}>
                  Authorized Users
                </Text>
                <Text lineHeight="tall" color="gray.700">
                  Only verified owners, admins, and staff linked to a facility may access related
                  data.
                </Text>
              </Box>
              <Box bg="blue.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="blue.700" mb={2}>
                  Service Providers
                </Text>
                <Text lineHeight="tall" color="gray.700">
                  Trusted third-party providers (cloud hosting, SMS/email gateways) may process data
                  strictly for operational purposes under confidentiality agreements.
                </Text>
              </Box>
              <Box bg="red.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="red.700" mb={2}>
                  Legal Obligations
                </Text>
                <Text lineHeight="tall" color="gray.700">
                  Disclosure is made when required by law or regulation.
                </Text>
              </Box>
            </VStack>
            <Box bg="gray.50" p={5} borderRadius="lg" mt={4}>
              <Text lineHeight="tall" color="gray.700" fontStyle="italic">
                All third parties are prohibited from retaining or using your data beyond their
                agreed purpose.
              </Text>
            </Box>
          </Box>

          {/* Section 5 */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
            borderLeft="4px solid"
            borderColor="red.400"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              5. Data Security
            </Heading>

            <VStack align="start" gap={3}>
              <HStack>
                <Box minW="8px" h="8px" borderRadius="full" bg="red.500" />
                <Text color="gray.700">
                  Passwords and session tokens are encrypted and securely stored
                </Text>
              </HStack>
              <HStack>
                <Box minW="8px" h="8px" borderRadius="full" bg="red.500" />
                <Text color="gray.700">HTTPS is used for all data transmission</Text>
              </HStack>
              <HStack>
                <Box minW="8px" h="8px" borderRadius="full" bg="red.500" />
                <Text color="gray.700">
                  Multi-factor authentication is implemented for administrative access
                </Text>
              </HStack>
              <HStack>
                <Box minW="8px" h="8px" borderRadius="full" bg="red.500" />
                <Text color="gray.700">
                  Access is role-based, and data is regularly backed up and audited
                </Text>
              </HStack>
            </VStack>
            <Box
              bg="yellow.50"
              p={5}
              borderRadius="lg"
              mt={4}
              borderLeft="3px solid"
              borderColor="yellow.400"
            >
              <Text lineHeight="tall" color="gray.700" fontWeight="medium">
                ⚠️ No system is fully immune to risk; users should protect their credentials.
              </Text>
            </Box>
          </Box>

          {/* Sections 6-13 in compact cards */}
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              6. Data Retention
            </Heading>
            <Text lineHeight="tall" color="gray.700">
              User and operational data is retained as long as the account is active or as required
              by law. Upon account deletion, data may be anonymized for compliance or reporting
              purposes. Users may request account deletion or data export via support (see Section
              12).
            </Text>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              7. Your Rights
            </Heading>
            <Text mb={3} color="gray.700" fontWeight="medium">
              Under the Digital Personal Data Protection Act, 2023 (DPDPA) and applicable law, you
              may:
            </Text>
            <VStack align="start" gap={2} pl={2}>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">Access or review your personal information</Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">Correct inaccuracies</Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">
                  Request deletion of personal data (subject to legal obligations)
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">Withdraw consent for communications (SMS, Email, FCM)</Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">
                  File complaints with the Data Protection Board of India for privacy violations
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <HStack mb={4}>
              <Icon fontSize="2xl" color="blue.500">
                <HiGlobe />
              </Icon>
              <Heading as="h2" size="lg" color="gray.800">
                8. Third-Party Services
              </Heading>
            </HStack>

            <Text mb={4} color="gray.700">
              The Platform integrates with certain third-party services:
            </Text>
            <VStack align="stretch" gap={4}>
              <Box bg="red.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="red.700" mb={2}>
                  Google Maps API
                </Text>
                <Text color="gray.700" mb={2}>
                  Maps and address resolution
                </Text>
                <Text fontSize="sm" color="blue.600">
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    View Privacy Policy →
                  </a>
                </Text>
              </Box>
              <Box bg="yellow.50" p={5} borderRadius="lg">
                <Text fontWeight="semibold" color="yellow.700" mb={2}>
                  Firebase Cloud Messaging (FCM)
                </Text>
                <Text color="gray.700" mb={2}>
                  Push notifications
                </Text>
                <Text fontSize="sm" color="blue.600">
                  <a
                    href="https://firebase.google.com/support/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    View Privacy Policy →
                  </a>
                </Text>
              </Box>
            </VStack>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              9. Hosting & Maintenance
            </Heading>
            <Text lineHeight="tall" color="gray.700">
              SheetX is hosted, maintained, and updated by{" "}
              <strong>Krishitantra (Klonec Automation Systems Pvt. Ltd.)</strong>. Technical
              support, updates, and Google Play distribution are managed by the publisher.
              Operational data remains under the control of Aura Tech Systems, while the publisher
              ensures secure app hosting and distribution.
            </Text>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              10. Permissions Used by the App
            </Heading>
            <VStack align="stretch" gap={3}>
              <Box bg="blue.50" p={4} borderRadius="lg">
                <Text fontWeight="semibold" color="blue.700">
                  📷 Camera
                </Text>
                <Text color="gray.700">For scanning QR codes of crates and shelves</Text>
              </Box>
              <Box bg="green.50" p={4} borderRadius="lg">
                <Text fontWeight="semibold" color="green.700">
                  📍 Location
                </Text>
                <Text color="gray.700">To display facility or customer locations (optional)</Text>
              </Box>
              <Box bg="purple.50" p={4} borderRadius="lg">
                <Text fontWeight="semibold" color="purple.700">
                  💾 Storage/Media Access
                </Text>
                <Text color="gray.700">To upload facility or commodity images</Text>
              </Box>
            </VStack>
            <Text mt={4} lineHeight="tall" color="gray.600" fontStyle="italic">
              Permissions are used only for intended app functionality.
            </Text>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              11. Children's Privacy
            </Heading>
            <Text lineHeight="tall" color="gray.700">
              The Platform is for authorized cold storage operators and staff only. SheetX is not
              intended for individuals under 18 years, and we do not knowingly collect data from
              minors.
            </Text>
          </Box>

          {/* Contact Section - Enhanced */}
          <Box
            bg="blue.500"
            bgGradient="linear(to-r, blue.500, purple.600)"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="xl"
            color="white"
          >
            <Heading as="h2" size="lg" mb={6} textAlign="center">
              12. Contact Us
            </Heading>
            <Text mb={6} textAlign="center" opacity={0.95}>
              For privacy-related questions or data requests:
            </Text>

            <Flex gap={6} flexWrap="wrap" justify="center">
              <Box
                bg="whiteAlpha.200"
                backdropFilter="blur(10px)"
                p={6}
                borderRadius={{ base: "lg", md: "xl" }}
                flex="1"
                minW="280px"
              >
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Data Controller
                </Text>
                <Text fontWeight="semibold" mb={2}>
                  Aura Tech Systems
                </Text>
                <VStack align="start" gap={1} fontSize="sm" opacity={0.95}>
                  <Text>📧 info@auratechsystems.com</Text>
                  <Text>
                    📍 No 842/1, 80 feet road, 29th main 33rd cross, Poorna Pragna layout
                    Uttarahalli, Bangalore South, Karnataka 560061
                  </Text>
                </VStack>
              </Box>

              <Box
                bg="whiteAlpha.200"
                backdropFilter="blur(10px)"
                p={6}
                borderRadius={{ base: "lg", md: "xl" }}
                flex="1"
                minW="280px"
              >
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Data Processor / Publisher
                </Text>
                <Text fontWeight="semibold" mb={2}>
                  Krishitantra
                </Text>
                <Text fontSize="sm" mb={2} opacity={0.9}>
                  (Klonec Automation Systems Pvt. Ltd.)
                </Text>
                <VStack align="start" gap={1} fontSize="sm" opacity={0.95}>
                  <Text>📧 info@krishitantra.com</Text>
                  <Text>
                    📍 2-53(1), Opposite of Janardana temple, Pangala, Udupi, Karnataka, India
                  </Text>
                </VStack>
              </Box>
            </Flex>

            <Text mt={6} textAlign="center" fontSize="sm" opacity={0.9}>
              Users may also contact us via the in-app Support section.
            </Text>
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="md"
          >
            <Heading as="h2" size="lg" color="gray.800" mb={4}>
              13. Policy Updates
            </Heading>
            <VStack align="start" gap={2} pl={2}>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">
                  We may update this Privacy Policy to reflect legal, technical, or operational
                  changes.
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">
                  Users will be notified of material changes via in-app alerts or email.
                </Text>
              </HStack>
              <HStack>
                <Box w="6px" h="6px" borderRadius="full" bg="blue.500" />
                <Text color="gray.700">
                  The latest version will always be accessible in the app and on our website.
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Footer Consent */}
          <Box
            bg="green.400"
            bgGradient="linear(to-r, green.400, teal.500)"
            p={{ base: 4, md: 8 }}
            borderRadius={{ base: "lg", md: "xl" }}
            shadow="xl"
            textAlign="center"
          >
            <Icon fontSize="4xl" color="white" mb={4}>
              <HiShieldCheck />
            </Icon>
            <Text fontSize="lg" fontWeight="bold" color="white" lineHeight="tall" mb={2}>
              By using SheetX, you consent to the collection, storage, and processing of your
              personal and operational data
            </Text>
            <Text color="white" opacity={0.95} lineHeight="tall">
              by both Aura Tech Systems and Krishitantra (Klonec Automation Systems Pvt. Ltd.) as
              described in this Privacy Policy.
            </Text>
          </Box>
        </VStack>
      </Container>

      {/* Bottom Spacing */}
      <Box h={16} />
    </Box>
  );
}
